// Opaque, map-independent cloud colors. Texture must never sample the hidden map.
let cached;
export function getFogTexture(size) {
  if (cached?.size === size) return cached.pixels;
  const random = (x, y) => {
    let value = (Math.imul(x, 374761393) + Math.imul(y, 668265263)) | 0;
    value = Math.imul(value ^ (value >>> 13), 1274126177);
    return ((value ^ (value >>> 16)) >>> 0) / 4294967295;
  };
  const fields = [6, 17, 43].map(scale => ({ scale,
    values: Float32Array.from({ length: (scale + 1) ** 2 }, (_, i) => random(i % (scale + 1), Math.floor(i / (scale + 1)))) }));
  const smooth = t => t * t * (3 - 2 * t);
  const sample = (field, u, v) => {
    const x = u * field.scale, y = v * field.scale;
    const left = Math.floor(x), top = Math.floor(y), stride = field.scale + 1;
    const sx = smooth(x - left), sy = smooth(y - top);
    const a = field.values[top * stride + left], b = field.values[top * stride + left + 1];
    const c = field.values[(top + 1) * stride + left], d = field.values[(top + 1) * stride + left + 1];
    return (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy;
  };
  const pixels = new Uint8ClampedArray(size * size * 4);
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const u = x / size, v = y / size;
    const cloud = sample(fields[0], u, v) * .6 + sample(fields[1], u, v) * .28 + sample(fields[2], u, v) * .12;
    const shade = 42 + Math.round(cloud * 100), offset = (y * size + x) * 4;
    pixels[offset] = shade; pixels[offset + 1] = shade + 7; pixels[offset + 2] = shade + 14;
    pixels[offset + 3] = 255;
  }
  cached = { size, pixels };
  return pixels;
}

// How far fog drifts onto revealed ground, as a share of a square. It wanders between these along the edge.
export const FOG_DRIFT = Object.freeze({ min: 0.08, max: 0.12 });

/**
 * Softens fog edges in RGBA `data`, where alpha is 255 on hidden pixels and 0 on revealed ones. Only revealed pixels change,
 * gaining a little fog near the edge: drift never thins hidden pixels, so it can't show anything under them.
 */
export function addFogDrift(data, size, columns, clouds) {
  const square = size / columns;
  const far = square * FOG_DRIFT.max + 2;
  const count = size * size;
  const distance = new Float32Array(count);
  let hidden = 0;
  for (let i = 0; i < count; i++) {
    distance[i] = data[i * 4 + 3] ? 0 : far;
    if (data[i * 4 + 3]) hidden++;
  }
  if (!hidden || hidden === count) return data;
  // Distance from the nearest hidden pixel, in one pass each way.
  const step = (i, next, cost) => { if (distance[next] + cost < distance[i]) distance[i] = distance[next] + cost; };
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const i = y * size + x;
    if (x) step(i, i - 1, 1);
    if (y) step(i, i - size, 1);
    if (x && y) step(i, i - size - 1, Math.SQRT2);
    if (y && x < size - 1) step(i, i - size + 1, Math.SQRT2);
  }
  for (let y = size - 1; y >= 0; y--) for (let x = size - 1; x >= 0; x--) {
    const i = y * size + x;
    if (x < size - 1) step(i, i + 1, 1);
    if (y < size - 1) step(i, i + size, 1);
    if (x < size - 1 && y < size - 1) step(i, i + size + 1, Math.SQRT2);
    if (y < size - 1 && x) step(i, i + size - 1, Math.SQRT2);
  }
  const shade = (x, y) => (clouds[(y % size * size + x % size) * 4] - 42) / 100;
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const i = y * size + x;
    if (data[i * 4 + 3] || distance[i] >= far) continue;
    const wander = Math.max(0, Math.min(1, (shade(x * 3, y * 3) * .6 + shade(x * 7, y * 7) * .4 - .35) / .3));
    const reach = square * (FOG_DRIFT.min + (FOG_DRIFT.max - FOG_DRIFT.min) * wander);
    const left = 1 - distance[i] / reach;
    if (left > 0) data[i * 4 + 3] = Math.round(255 * left * left * (3 - 2 * left));
  }
  return data;
}
